/**
 * Utilities for constructing demo and embed subdomain URLs from env vars.
 * Uses DEMO_SUBDOMAIN and EMBED_SUBDOMAIN (e.g. reach-demo, reach-embed) with
 * the root domain derived from NEXT_PUBLIC_APP_URL.
 */

/** Main Reach app origin (e.g. https://reach.kindway.ai) for redirects from demo subdomain. */
export function getMainAppOrigin(): string {
  const appUrl = process.env.NEXT_PUBLIC_APP_URL;
  if (appUrl) {
    try {
      return new URL(appUrl).origin;
    } catch {
      /* fall through */
    }
  }
  return 'https://reach.kindway.ai';
}

function getRootDomain(): string {
  const appUrl = process.env.NEXT_PUBLIC_APP_URL;
  if (!appUrl) return 'kindway.ai';
  try {
    const hostname = new URL(appUrl).hostname.toLowerCase();
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return parts.slice(-2).join('.');
    }
    return hostname;
  } catch {
    return 'kindway.ai';
  }
}

/**
 * Get the demo subdomain base URL (e.g. https://reach-demo.kindway.ai)
 * Uses DEMO_SUBDOMAIN (server) or NEXT_PUBLIC_DEMO_SUBDOMAIN (client)
 */
export function getDemoBaseUrl(): string {
  const subdomain =
    process.env.DEMO_SUBDOMAIN?.toLowerCase() ??
    process.env.NEXT_PUBLIC_DEMO_SUBDOMAIN?.toLowerCase() ??
    'demo';
  const root = getRootDomain();
  return `https://${subdomain}.${root}`;
}

/**
 * Get the embed subdomain base URL (e.g. https://reach-embed.kindway.ai)
 * Uses EMBED_SUBDOMAIN (server) or NEXT_PUBLIC_EMBED_SUBDOMAIN (client)
 */
export function getEmbedBaseUrl(): string {
  const subdomain =
    process.env.EMBED_SUBDOMAIN?.toLowerCase() ??
    process.env.NEXT_PUBLIC_EMBED_SUBDOMAIN?.toLowerCase() ??
    'embed';
  const root = getRootDomain();
  return `https://${subdomain}.${root}`;
}

/**
 * Get demo subdomain name (for client-side checks, use NEXT_PUBLIC_DEMO_SUBDOMAIN)
 */
export function getDemoSubdomain(): string {
  return (
    process.env.NEXT_PUBLIC_DEMO_SUBDOMAIN ??
    process.env.DEMO_SUBDOMAIN?.toLowerCase() ??
    'demo'
  );
}

/**
 * Get embed subdomain name (for client-side checks, use NEXT_PUBLIC_EMBED_SUBDOMAIN)
 */
export function getEmbedSubdomain(): string {
  return (
    process.env.NEXT_PUBLIC_EMBED_SUBDOMAIN ??
    process.env.EMBED_SUBDOMAIN?.toLowerCase() ??
    'embed'
  );
}

/** True when `hostname` is the embed subdomain (e.g. embed.kindway.ai). */
export function isEmbedSubdomainHost(hostname: string): boolean {
  const host = hostname.split(':')[0].toLowerCase();
  const embedSub = getEmbedSubdomain().toLowerCase();
  const parts = host.split('.');

  if (host.includes('localhost')) {
    return parts[0] === embedSub;
  }

  if (parts.length >= 3 && !/^\d+\.\d+\.\d+\.\d+/.test(host)) {
    return parts[0] === embedSub;
  }

  return false;
}
